import {treeRelativePath} from "seo/documentLang";
import {ADVERTISING_TERMS_PATH} from "sponsor/sponsorRoutes";

/** The event fields that distinguish in-place activation from native new-tab/window behavior. */
export interface PrimaryClickLike {
	button: number;
	metaKey: boolean;
	ctrlKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
}

/** Plain primary activation may be progressively enhanced; every other activation stays native. */
export function isPlainPrimaryClick(click: PrimaryClickLike): boolean {
	return click.button === 0 && !click.metaKey && !click.ctrlKey && !click.shiftKey && !click.altKey;
}

/**
 * The root-absolute app path an anchor leads to, or `null` when it leads off this origin.
 *
 * Read from the anchor's RESOLVED properties rather than from its `href` ATTRIBUTE, and that is the
 * whole reason this exists: since ui#2732 a link inside a locale mirror carries a path relative to the
 * document (`./client-server`), because one artifact serves both `transferify.ro/about` and
 * `transferify.eu/ro/about`. A handler reading the attribute sees a string that starts with `.`,
 * decides it is external, and lets the browser navigate away — which is a dialog that stops swapping
 * its body and reloads the page instead. The browser has already resolved the address; ask it.
 *
 * An anchor whose scheme has no origin (`mailto:`, `tel:`) reports an origin that matches nothing here,
 * so it answers `null` and stays native, which is the same answer the attribute check used to give it.
 */
export function anchorAppPath(anchor: HTMLAnchorElement, origin: string): string | null {
	return anchor.origin === origin ? `${anchor.pathname}${anchor.search}${anchor.hash}` : null;
}

/**
 * Primary product routes in the Explore column.
 *
 * Home is NOT here since ui#780: it is a permanent pill in the bar itself, in both auth states, and an
 * entry here as well would be the same destination offered twice on one surface. Every link that
 * remains opens a dialog body, which is what this column is for.
 *
 * The column renders ONE row that is not in this table: Guides (ui#1555), which `NavBar` appends after
 * these. It is not a member because every entry here maps a FIXED path to a {@link ResourceKind} — that
 * is the pairing {@link resolveResourceDialogTarget} reads — and the guides row's path is the locale's
 * own localized slug, resolved per locale from the content corpus by `content/guideCatalogue`. Support,
 * Cookie Policy and Cookie Settings are the same shape: dropdown rows that open a dialog without being
 * a route this table can name.
 */
export const EXPLORE_RESOURCE_LINKS = [
	{to: "/about", labelKey: "about", resource: "about"},
	{to: "/p2p", labelKey: "p2p", resource: "p2p"},
	{to: "/client-server", labelKey: "clientServer", resource: "clientServer"},
	{to: "/zero-knowledge", labelKey: "zeroKnowledge", resource: "zeroKnowledge"},
	{to: "/contact", labelKey: "contact", resource: "contact"},
] as const;

/**
 * The Advertising column.
 *
 * One entry, and deliberately a column of its own rather than a sixth row under Explore: selling a
 * takeover is a different job from explaining how transfers work, and the owner asked for it to read
 * as its own offer. It replaces the ProfileMenu's "Advertise with us" row, which was reachable only
 * after signing in — an offer aimed at people who have never used the product cannot live behind a
 * login, so this column renders in BOTH auth states like the rest of the disclosure.
 *
 * `resource: null` since ui#1861, and that is the whole of the owner's "it can open in a new page": the
 * row NAVIGATES to `/advertising` rather than opening its body in `ResourceDialog`. The page grew a
 * full-width hero, a six-treatment gallery and a live availability row, none of which a modal sheet can
 * hold, so there is no body left to render inside one. `null` is the same value Home carried before
 * ui#780 moved it to the bar, and it flows through the whole disclosure unchanged: `NavBar` lets the
 * anchor's own click through, {@link resolveResourceDialogTarget} falls through to `{type: "navigate"}`,
 * and `e2e/publicNavigationContract.ts` drops the row from its dialog list.
 *
 * `labelKey` stays: the ROW still says what you can do ("Advertise with us"), and that is catalog
 * identity rather than copy.
 */
export const ADVERTISING_RESOURCE_LINKS = [
	{to: "/advertising", labelKey: "advertise", resource: null},
] as const;

/**
 * Crawlable policy routes in the Support & legal column.
 *
 * The cancellation row is `resource: null`, and that is the one thing about it that is load-bearing: § 312k
 * Abs. 2 S. 3 BGB requires the control to lead the consumer to the confirmation page "unmittelbar", so the
 * row NAVIGATES to the real page instead of opening a body inside the dialog it was clicked from. It is also
 * why there is no `RESOURCE_BODIES` entry for it and nothing to render one from.
 *
 * It sits FIRST in this column deliberately. LG München I 3 HK O 13796/24 held that a cancellation control
 * buried among many links is not "leicht zugänglich"; first in the legal column is the most visible place
 * this disclosure has for it. That court's stronger reading — that consumers look for it where the
 * commercial terms are discussed — would mean a second placement on the pricing page, which is FILED rather
 * than smuggled in here, because a second top-level control is an information-architecture decision.
 *
 * The Impressum carries `resource: null`, the same value `/advertising` does, so the row NAVIGATES to
 * the page instead of opening its body in `ResourceDialog` (kitecorp/transferify-ui#2291). That is
 * what the page is for: § 5 DDG asks for provider identification that is easily recognisable,
 * directly accessible and permanently available, and a dialog is a surface a reader cannot link to,
 * bookmark or hand to anyone else. The row is present in every locale because the identification is
 * the same company in every locale; only German law makes the page mandatory.
 *
 * The Advertising Terms row (kitecorp/transferify-ui#2602) is `resource: null` for the same reason:
 * an advertiser must be able to open, save and print the version they contracted under (§ 312i
 * Abs. 1 Nr. 4 BGB), and the booking form and the pay step link the same address. It sits right
 * after the Terms because it is the booking contract's own terms, and a reader looking for one will
 * look beside the other. It is in this column and NOT in the footer: see {@link FOOTER_LEGAL_LINKS}.
 */
export const LEGAL_RESOURCE_LINKS = [
	{to: "/cancel-contract", labelKey: "cancelContract", resource: null},
	{to: "/terms", labelKey: "terms", resource: "terms"},
	{to: ADVERTISING_TERMS_PATH, labelKey: "advertisingTerms", resource: null},
	{to: "/privacy", labelKey: "privacy", resource: "privacy"},
	{to: "/impressum", labelKey: "impressum", resource: null},
] as const;

/**
 * The rows the site footer renders: the legal column WITHOUT the Advertising Terms.
 *
 * The footer stays four links, and that is a measured constraint rather than taste. A fifth label
 * wraps the English row to two lines at 375px, the create card gives up that height to the footer's
 * reserve (`SITE_FOOTER_RESERVE_PROPERTY`), and the gift tag's pull ended 18% off screen at 375x812
 * (`personalization-preview-first.spec.ts`, red on the ui#2602 pull request). The legal reason the
 * footer exists is the § 312k cancellation control, and the Advertising Terms have their own places:
 * this column, the sitemap, llms.txt, and the two in-flow links an advertiser meets before submitting
 * and before paying, which is where the contract actually needs them.
 */
export const FOOTER_LEGAL_LINKS = LEGAL_RESOURCE_LINKS.filter((link) => link.to !== ADVERTISING_TERMS_PATH);

/** Every real crawlable route shown by the Resources disclosure. */
export const RESOURCE_LINKS = [
	...EXPLORE_RESOURCE_LINKS,
	...ADVERTISING_RESOURCE_LINKS,
	...LEGAL_RESOURCE_LINKS,
] as const;

export type ResourceKind = Exclude<(typeof RESOURCE_LINKS)[number]["resource"], null>;

export type ResourceDialogTarget =
	| {type: "resource"; resource: ResourceKind}
	| {type: "features"; focus: "faq" | null}
	| {type: "pricing"}
	| {type: "cookie"}
	| {type: "navigate"; href: string}
	| {type: "native"};

/**
 * Resolve a link activated inside a resource dialog. Only root-relative links are intercepted;
 * external URLs and protocols such as `mailto:` retain browser-native behavior.
 */
export function resolveResourceDialogTarget(href: string): ResourceDialogTarget {
	if (!href.startsWith("/") || href.startsWith("//")) {
		return {type: "native"};
	}

	const [pathAndSearch, hash = ""] = href.split("#", 2);
	// Tree-relative, because the SAME page has two addresses: `/client-server` on a brand domain and
	// `/ro/client-server` inside the main domain's Romanian mirror, which is what a link rendered there
	// now carries (ui#2498). Looking the table up by the raw path would stop recognising every one of
	// these links under a prefix, and the dialog would navigate away instead of swapping its body.
	const pathname = treeRelativePath(pathAndSearch.split("?", 1)[0]);
	/*
	 * `!= null` catches BOTH cases, and both are live again since ui#1861 made `/advertising` a plain
	 * navigation target. `undefined` is a path this table does not list at all; `null` is a path it lists
	 * with no dialog body. Either way the answer is the same — fall through and navigate — and writing it
	 * as `!== undefined` would return `{type: "resource", resource: null}` for the advertising row, which
	 * `RESOURCE_BODIES` has nothing to render for. That is exactly how the row behaved before ui#780
	 * removed Home, the previous null-resource entry.
	 */
	const resource = RESOURCE_LINKS.find(link => link.to === pathname)?.resource;
	if (resource != null) {
		return {type: "resource", resource};
	}
	if (pathname === "/features") {
		return {type: "features", focus: hash === "features-faq-heading" ? "faq" : null};
	}
	if (pathname === "/pricing") {
		return {type: "pricing"};
	}
	if (pathname === "/cookies") {
		return {type: "cookie"};
	}
	return {type: "navigate", href};
}
